import { action, computed, observable, runInAction } from 'mobx';
import { ISuggestionItem } from 'telia-front-react';
import { debounce } from 'throttle-debounce';
import ErrorUtil from '../error/ErrorUtil';
import AddressStore, { Address, Room } from './AddressStore';
import RootStore from './RootStore';
import SegmentStore, { SEGMENT_BUSINESS } from './SegmentStore';

class AddressAutocompleteStore {
  @observable accessor selectedAddress: Address;
  @observable accessor addressText: string = '';
  @observable accessor roomText: string = '';
  @observable accessor isApartmentInvalid: boolean = false;
  @observable accessor roomField: HTMLInputElement | HTMLTextAreaElement = null;
  @observable accessor error: string = null;

  @observable accessor addressAutocompleteItems: ISuggestionItem[] = [];
  @observable accessor roomSuggestionsItems: ISuggestionItem[] = [];
  @observable accessor allRoomOptions: ISuggestionItem[] = [];
  @observable accessor selectedAddressItem: ISuggestionItem;

  private addressStore: AddressStore;
  private segmentStore: SegmentStore;

  private readonly debouncedFetchAddressAutocompleteOptions: () => void;

  constructor(rootStore: RootStore) {
    this.addressStore = rootStore.addressStore;
    this.segmentStore = rootStore.segmentStore;

    this.debouncedFetchAddressAutocompleteOptions = debounce(250, this.fetchAddressAutocompleteOptions);
  }

  @computed
  public get suggestions() {
    return this.addressAutocompleteItems.slice();
  }

  @computed
  public get roomSuggestions() {
    return this.roomSuggestionsItems.slice();
  }

  public onAddressAutocompleteChange = (searchTerm: string) => {
    runInAction(() => {
      if (this.addressText === searchTerm) {
        return;
      }
      this.addressText = searchTerm;
      this.selectedAddress = null;
      this.allRoomOptions = [];
      this.roomSuggestionsItems = [];
      this.roomText = '';
      if (!this.findSuggestionItemByLabel(this.suggestions, this.addressText)) {
        this.debouncedFetchAddressAutocompleteOptions();
      }
    });
  };

  public onAddressAutocompleteSelectAddress = (item: ISuggestionItem) => {
    runInAction(() => {
      this.selectedAddressItem = item;
      this.onAddressAutocompleteSelection(true);
    });
  };

  public clearAddress = () => {
    runInAction(() => {
      this.addressAutocompleteItems = [];
      this.addressText = '';
      this.selectedAddress = null;
      this.allRoomOptions = [];
      this.roomSuggestionsItems = [];
      this.error = '';
      this.isApartmentInvalid = false;
    });
  };

  public onAddressAutocompleteFocus = () => {
    runInAction(() => {
      this.isApartmentInvalid = false;
    });
  };

  public onRoomAutocompleteChange = (value: string) => {
    runInAction(() => {
      this.roomText = value;
      this.clearError();

      if (!value) {
        this.roomSuggestionsItems = this.allRoomOptions;
        return;
      }

      this.roomSuggestionsItems = this.allRoomOptions.filter(
        (room) => room.label.startsWith(value, 0) && room.label.toLowerCase().includes(value.toLocaleLowerCase())
      );
    });
  };

  public setAddressFieldsByIds = async (addressId: string, buildingId: string, room: Room) => {
    const addressById = await this.addressStore.getAddressById(addressId, buildingId);
    runInAction(() => {
      this.selectedAddress = addressById;
      this.addressText = addressById.label;
      this.roomText = addressById.rooms?.find((r) => r.roomId === room?.roomId)?.roomName;
      this.allRoomOptions = this.sortAndMapRoomsToSuggestionItems(this.selectedAddress.rooms);
      this.roomSuggestionsItems = this.allRoomOptions;
    });
  };

  private clearError = () => {
    runInAction(() => {
      this.error = null;
    });
  };

  public fetchAddressAutocompleteOptions = () => {
    this.clearError();
    return this.addressStore
      .fetchAddressResult(this.addressText)
      .then((addresses) => {
        runInAction(() => {
          this.addressAutocompleteItems = addresses.map((address: Address) => ({
            id: `${address.id}-${address.buildingId}`,
            url: '#',
            label: address.label,
            isActive: address.label === this.addressText,
            onClick: this.clearError
          }));
        });
        this.findSuggestionItemByLabel(this.suggestions, this.addressText) && this.onAddressAutocompleteSelection();
      })
      .catch((e) => ErrorUtil.pushErrorToTeliaRumAndLog(e));
  };

  private onAddressAutocompleteSelection = async (isSelect = false) => {
    runInAction(() => {
      if (!isSelect) {
        this.selectedAddressItem = this.findSuggestionItemByLabel(this.suggestions, this.addressText);
      }
      this.allRoomOptions = [];
      this.roomText = '';
    });
    if (this.selectedAddressItem) {
      const [addressId, buildingId] = this.selectedAddressItem.id.split('-');
      const result = await this.addressStore.getAddressById(addressId, buildingId);
      runInAction(() => {
        this.selectedAddress = result;
        if (this.selectedAddress.rooms?.length) {
          this.allRoomOptions = this.sortAndMapRoomsToSuggestionItems(this.selectedAddress.rooms);
          this.roomSuggestionsItems = this.allRoomOptions;
          this.roomField && this.roomField.focus();
        }
      });
    }
  };

  public findRoom = (): Room => {
    const roomOption = this.findSuggestionItemByLabel(this.allRoomOptions, this.roomText);
    return roomOption ? { roomId: roomOption.id, roomName: roomOption.label } : { roomName: this.roomText };
  };

  @action
  public handleApartmentNotice() {
    const isB2B = this.segmentStore.currentSegment === SEGMENT_BUSINESS;
    this.isApartmentInvalid = this.allRoomOptions.length && !this.findRoom().roomId;
    if (this.isApartmentInvalid) {
      this.error = isB2B ? 'mandatoryRoom.B2B' : 'mandatoryRoom';
    }
  }

  private findSuggestionItemByLabel = (suggestions: ISuggestionItem[], label: string) => {
    return suggestions.find((item) => item.label === label);
  };

  public sortAndMapRoomsToSuggestionItems = (rooms: Room[]): ISuggestionItem[] => {
    return rooms
      .map((room: Room) => ({
        label: room.roomName,
        id: room.roomId,
        onClick: this.clearError,
        url: '#'
      }))
      .sort((a: any, b: any) => parseInt(a.label, 10) - parseInt(b.label, 10));
  };
}

export default AddressAutocompleteStore;
